/**
 * AdditionalCopiesConfirmation — Mail PR A FE
 *
 * Renders the per-folder outcome of `response.additional_copies` shipped
 * by the backend in PR #414, alongside the canonical Attendances
 * confirmation.  Three statuses (per backend contract):
 *
 *   - "copied"      → ✓  Copied to <folder>.  web_url link if present.
 *   - "in_progress" → …  Copy still in progress.  monitor_url link if present.
 *   - "failed"      → ✗  Copy failed — error rendered via window.errorMessage.
 *
 * Hard rule (Vanessa): if ANY copy failed, the surrounding success state
 * MUST visibly surface the failure — no green-tick-only "all good" state.
 * This component renders the failures honestly; callers pick the wrapping
 * tone (success vs. mixed) using `anyFailed` / `anyInProgress` exports.
 */
import { CheckCircle2, AlertTriangle, AlertCircle, ExternalLink, Loader2 } from "lucide-react";
import { errorMessage } from "../utils/errorMessage";

function _formatError(err, fallback) {
  if (typeof err === "string") return err || fallback;
  return errorMessage(err, fallback);
}

export function summariseAdditionalCopies(additionalCopies) {
  const copies = Array.isArray(additionalCopies) ? additionalCopies : [];
  return {
    total: copies.length,
    copied: copies.filter((c) => c?.status === "copied").length,
    failed: copies.filter((c) => c?.status === "failed").length,
    inProgress: copies.filter((c) => c?.status === "in_progress").length,
    anyFailed: copies.some((c) => c?.status === "failed"),
    anyInProgress: copies.some((c) => c?.status === "in_progress"),
  };
}

export default function AdditionalCopiesConfirmation({
  canonicalLabel = "Attendances",
  additionalCopies = [],
  compact = false,
}) {
  const copies = Array.isArray(additionalCopies) ? additionalCopies : [];
  const sizeClass = compact ? "text-xs" : "text-sm";

  return (
    <div
      data-testid="additional-copies-confirmation"
      className={`flex flex-col gap-1 ${sizeClass}`}
    >
      <div
        data-testid="additional-copies-canonical"
        className="flex items-center gap-2 text-status-green"
      >
        <CheckCircle2 className="w-4 h-4" />
        <span>Filed to {canonicalLabel} ✓</span>
      </div>

      {copies.map((c, i) => {
        const folderName = c?.folder_name || c?.folder_id || "selected folder";
        if (c?.status === "copied") {
          return (
            <div
              key={i}
              data-testid="additional-copy-row-copied"
              className="flex items-center gap-2 text-status-green"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Copied to {folderName} ✓</span>
              {c?.web_url && (
                <a
                  href={c.web_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-400 hover:underline inline-flex items-center gap-0.5"
                  data-testid="additional-copy-weburl"
                >
                  <ExternalLink className="w-3 h-3" /> Open
                </a>
              )}
            </div>
          );
        }
        if (c?.status === "in_progress") {
          return (
            <div
              key={i}
              data-testid="additional-copy-row-in-progress"
              className="flex items-center gap-2 text-status-amber"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Copy to {folderName} still copying…</span>
              {c?.monitor_url && (
                <a
                  href={c.monitor_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-400 hover:underline inline-flex items-center gap-0.5"
                  data-testid="additional-copy-monitor-url"
                >
                  <ExternalLink className="w-3 h-3" /> Check status
                </a>
              )}
            </div>
          );
        }
        if (c?.status === "failed") {
          return (
            <div
              key={i}
              data-testid="additional-copy-row-failed"
              className="flex items-center gap-2 text-status-red"
            >
              <AlertCircle className="w-4 h-4" />
              <span>
                Copy to {folderName} failed —{" "}
                {_formatError(c?.error, "unknown error")}
              </span>
            </div>
          );
        }
        // Unknown status — surface honestly rather than silently hiding.
        return (
          <div
            key={i}
            data-testid="additional-copy-row-unknown"
            className="flex items-center gap-2 text-status-amber"
          >
            <AlertTriangle className="w-4 h-4" />
            <span>
              Copy to {folderName}: {_formatError(c?.status, "unknown status")}
            </span>
          </div>
        );
      })}
    </div>
  );
}
